import { Link, Navigate, useParams } from 'react-router-dom';
import type { SharedStoryDoc } from '@spirits/shared';
import { useDoc } from '../hooks/useDoc';
import { RecapView } from '../play/RecapView';

// Standalone, world-readable recap page (route /s/:storyId). Renders the mirror
// in /sharedStories — no game chrome, shareable as-is.
export default function SharedRecapRoute() {
  const { storyId } = useParams<{ storyId: string }>();
  if (!storyId) return <Navigate to="/" replace />;
  return <SharedRecap storyId={storyId} />;
}

function SharedRecap({ storyId }: { storyId: string }) {
  const { data: shared, loading } = useDoc<SharedStoryDoc>(`sharedStories/${storyId}`);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">
        <p className="text-xl tracking-wider">unrolling the tale…</p>
      </div>
    );
  }
  if (!shared) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-950 text-slate-400">
        <p className="text-lg">no tale lives at this link.</p>
        <Link to="/" className="text-amber-200/90 hover:underline">visit Spirits →</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="px-6 py-3 border-b border-slate-900 flex items-center justify-between">
        <Link to="/" className="text-2xl tracking-wide text-slate-100 hover:text-amber-100">Spirits</Link>
        <span className="text-[11px] uppercase tracking-[0.25em] text-amber-300/70">a told tale ✦</span>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <RecapView
          title={shared.title}
          blurb={shared.blurb}
          beats={shared.beats}
          closing={shared.closing}
          storyTitle={shared.storyTitle}
          category={shared.category}
          endingTitle={shared.endingTitle}
        />
        <div className="mt-10 text-center">
          <Link
            to="/"
            className="inline-block rounded-lg border border-amber-400/50 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40 px-4 py-2 text-sm"
          >
            Steer your own story →
          </Link>
        </div>
      </main>
    </div>
  );
}
